import { forwardRef, useMemo } from 'react'
import { NavLink, type NavLinkProps } from 'react-router'

import { useLocale } from './LocaleProvider'
import { localizeUrl, stripLocaleFromPath } from './utils'


export interface LocalizedNavLinkProps extends Omit<NavLinkProps, 'to'> {
  to: string
  /** Skip locale prefix (for special cases) */
  skipLocale?: boolean
}

export const LocalizedNavLink = forwardRef<HTMLAnchorElement, LocalizedNavLinkProps>(
  function LocalizedNavLink ({ to, skipLocale = false, ...props }, ref) {
    const { locale } = useLocale()

    const localizedTo = useMemo(() => {
      if (skipLocale) {
        return to
      }

      // Handle external URLs
      if (to.startsWith('http://') || to.startsWith('https://') || to.startsWith('//')) {
        return to
      }

      // Handle hash-only links
      if (to.startsWith('#')) {
        return to
      }

      // Strip existing locale and add current one
      const cleanPath = stripLocaleFromPath(to)
      return localizeUrl(cleanPath, locale)
    }, [ to, locale, skipLocale ])

    return <NavLink ref={ref} to={localizedTo} {...props} />
  },
)
